import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { initUiStore } from '@/store/ui-store';
import { initModeStore } from '@/store/mode-store';
import i18n, { SUPPORTED_LOCALES, type Locale } from '@/i18n';
import { configDb } from '@/persistence/config-db';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

async function loadStoredLocale(): Promise<void> {
  try {
    const pref = await configDb.preferences.get('locale');
    const value = pref?.value;
    if (
      value &&
      (SUPPORTED_LOCALES as readonly string[]).includes(value)
    ) {
      await i18n.changeLanguage(value as Locale);
    }
  } catch (err) {
    // Locale fall-through: i18n already initialised with browser default.
    console.warn('locale load failed', err);
  }
}

const CONFIG_DB_NAME = 'ExcelToChatsConfigDB';

/**
 * Drop the on-disk IndexedDB by name using the raw API. Dexie's
 * `db.delete()` works in most cases but the singleton can keep
 * stale schema state in memory after a failed `open()`, so a
 * follow-up reopen may hit the same UpgradeError. Going through
 * `indexedDB.deleteDatabase` sidesteps that — once the request
 * resolves the disk slot is empty and Dexie's next open fresh-creates
 * the v11 schema with no upgrades to run.
 *
 * Resolves on success, blocked, or error — every outcome leaves the
 * caller free to retry the Dexie open.
 */
function rawDeleteIndexedDb(name: string): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = settle;
      req.onerror = settle;
      req.onblocked = settle;
    } catch {
      settle();
    }
    // Hard timeout — some browsers leave deleteDatabase hanging when
    // another tab holds the connection. Resolve anyway after 2s so
    // boot doesn't lock the user out forever.
    setTimeout(settle, 2000);
  });
}

/**
 * Recover from a poisoned IndexedDB schema. The v7 → v11 PK migration
 * (D-020 follow-up) targets users who upgraded from v6; users who landed
 * on the original-buggy v7 (PK = `key`) before the rollback have a disk
 * schema that no declared version matches. Wiping is a safe last resort:
 * config data regenerates and dashboards can be re-imported from JSON.
 */
async function ensureConfigDbOpens(): Promise<void> {
  try {
    await configDb.open();
    return;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.warn('[config-db] open failed; wiping for recovery:', msg);
  }
  // Force-close any half-open connection so Dexie's internal state
  // doesn't carry over into the next open attempt.
  try {
    configDb.close();
  } catch {
    // Already closed / never opened — fine.
  }
  await rawDeleteIndexedDb(CONFIG_DB_NAME);
  try {
    await configDb.open();
  } catch (recoverErr) {
    console.error('[config-db] recovery wipe failed', recoverErr);
  }
}

// Hydrate theme + locale + mode from IndexedDB before first paint, then render.
void ensureConfigDbOpens().then(() =>
  Promise.all([
    initUiStore(),
    loadStoredLocale(),
    initModeStore(),
  ]),
).then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
